import React, { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { LanguageContext } from "../context/LanguageContext";
import styled from "styled-components";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";

const ArticleContainer = styled.article`
  max-width: 900px;
  margin: 0 auto;
  padding: 80px 20px;
  min-height: 80vh;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 30px;
  transition: opacity 0.3s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CoverImage = styled.img`
  width: 100%;
  max-height: 450px;
  object-fit: cover;
  border-radius: 24px;
  margin-bottom: 40px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
`;

const Title = styled.h1`
  font-size: 2.8rem;
  color: var(--primary);
  margin-bottom: 15px;
  line-height: 1.3;
`;

const MetaData = styled.div`
  display: flex;
  gap: 20px;
  color: var(--text-color);
  opacity: 0.7;
  font-size: 0.95rem;
  margin-bottom: 40px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 20px;
`;

const Content = styled.div`
  font-size: 1.15rem;
  line-height: 1.8;
  color: var(--text-color);

  h2, h3, h4 {
    color: var(--primary);
    margin: 30px 0 15px;
  }

  img {
    max-width: 100%;
    border-radius: 12px;
    margin: 20px 0;
  }

  a {
    color: var(--secondary);
  }
`;

export default function BlogPost() {
  const { id } = useParams();
  const { language } = useContext(LanguageContext);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const url = window.location.hostname === 'localhost' ? `http://localhost/api/blog.php?id=${id}` : `/api/blog.php?id=${id}`;
        const res = await axios.get(url);
        if (res.data.status === 'success') {
          setPost(res.data.post);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  if (loading) return <div style={{textAlign:'center', marginTop:'100px'}}><div className="spin" style={{display:'inline-block', fontSize:'40px', color: 'var(--primary)'}}>⚙</div></div>;
  
  if (!post) return <div style={{textAlign:'center', marginTop:'100px'}}><h2>{language === 'ar' ? 'المقال غير موجود' : 'Post not found'}</h2></div>;

  const title = language === 'ar' ? post.title_ar : post.title_en;
  const content = language === 'ar' ? post.content_ar : post.content_en;

  return (
    <ArticleContainer dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{title} | CodiCal</title>
        <meta name="description" content={post.seo_description} />
        <meta name="keywords" content={post.seo_keywords} />
        {/* Open Graph Meta Tags for beautiful sharing */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={post.seo_description} />
        {post.image_url && <meta property="og:image" content={window.location.origin + post.image_url} />}
      </Helmet>

      <BackLink to="/blog">
        {language === 'ar' ? <><FaArrowRight /> العودة للمدونة</> : <><FaArrowLeft /> Back to Blog</>}
      </BackLink>

      {post.image_url && <CoverImage src={post.image_url} alt={title} />}
      
      <Title>{title}</Title>
      
      <MetaData>
        <span>📅 {new Date(post.created_at).toLocaleDateString()}</span>
      </MetaData>

      <Content dangerouslySetInnerHTML={{ __html: content }} />
    </ArticleContainer>
  );
}
